import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, HUB_META } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import {
  ArrowLeft,
  Users,
  PaperPlaneTilt,
  Broadcast,
  Stop,
  Play,
  Record,
  Microphone,
  VideoCameraSlash,
} from "@phosphor-icons/react";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function LiveRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [recording, setRecording] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const chatScrollRef = useRef(null);

  const load = async () => {
    const [sx, px, cx] = await Promise.all([
      api.get(`/sessions/${id}`),
      api.get(`/sessions/${id}/participants`),
      api.get(`/sessions/${id}/chat`),
    ]);
    setS(sx.data);
    setParticipants(px.data);
    setMsgs(cx.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // join session
  useEffect(() => {
    if (s && user && user.id) {
      api.post(`/sessions/${id}/join`).catch(() => {});
    }
    // eslint-disable-next-line
  }, [s?.id, user?.id]);

  // poll chat + participants every 4s while session is live
  useEffect(() => {
    if (!s || s.status !== "live") return;
    const t = setInterval(async () => {
      const [px, cx] = await Promise.all([
        api.get(`/sessions/${id}/participants`),
        api.get(`/sessions/${id}/chat`),
      ]);
      setParticipants(px.data);
      setMsgs(cx.data);
    }, 4000);
    return () => clearInterval(t);
  }, [s?.status, id]);

  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight });
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const text = input.trim();
    setInput("");
    try {
      const { data } = await api.post(`/sessions/${id}/chat`, { body: text });
      setMsgs((m) => [...m, data]);
    } catch (_) {}
  };

  const startHosting = async () => {
    setBusy(true);
    try {
      // request camera + mic
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setStreaming(true);
      await api.post(`/sessions/${id}/start`);
      await load();
    } catch (e) {
      alert("Camera/mic permission required to go live.");
    } finally {
      setBusy(false);
    }
  };

  const stopRecording = () =>
    new Promise((resolve) => {
      if (!recorderRef.current) return resolve(null);
      recorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const fd = new FormData();
        fd.append("file", new File([blob], `session-${id}.webm`, { type: "video/webm" }));
        try {
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          resolve(data.url);
        } catch (_) { resolve(null); }
      };
      recorderRef.current.stop();
    });

  const toggleRecord = async () => {
    if (recording) {
      setRecording(false);
      await stopRecording();
    } else {
      if (!streamRef.current) return;
      chunksRef.current = [];
      const rec = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    }
  };

  const endSession = async () => {
    setBusy(true);
    let recordingUrl = "";
    if (recording) {
      recordingUrl = (await stopRecording()) || "";
      setRecording(false);
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setStreaming(false);
    try {
      await api.post(`/sessions/${id}/end`, { recording_url: recordingUrl });
      await load();
    } finally { setBusy(false); }
  };

  if (!s) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2" />LOADING SESSION...</div></AppShell>;

  const meta = HUB_META[s.hub_key];
  const isHost = user && user.id === s.host_id;
  const recordingSrc = s.recording_url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${s.recording_url}` : s.recording_url;

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-6" data-testid="live-room-page" style={{ minHeight: "calc(100vh - 6rem)" }}>
        {/* Stage */}
        <section className="lg:col-span-8 flex flex-col">
          <button onClick={() => nav("/live")} className="btn btn-ghost btn-sm mb-3 self-start">
            <ArrowLeft size={12} weight="bold" /> All sessions
          </button>

          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="label-mono mb-1 flex items-center gap-1.5" style={{ color: meta?.color }}>
                <Broadcast size={11} weight="fill" /> {meta?.short} · {s.hub_key.toUpperCase()}
              </div>
              <h1 className="font-display text-white" style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.025em" }}>{s.topic}</h1>
            </div>
            <div className="flex items-center gap-2">
              {s.status === "live" ? (
                <span className="label-mono px-2 py-1 border border-[#E60012] bg-[#E60012] text-white flex items-center gap-1.5">
                  <span className="hub-dot pulse" style={{ background: "#fff", width: 6, height: 6 }} /> LIVE
                </span>
              ) : s.status === "ended" ? (
                <span className="label-mono px-2 py-1 border border-[#27272A] text-zinc-400">ENDED</span>
              ) : (
                <span className="label-mono px-2 py-1 border border-[#27272A]" style={{ color: "#00E5FF" }}>SCHEDULED</span>
              )}
            </div>
          </div>

          {/* Video stage */}
          <div className="flex-1 border border-[#27272A] bg-black relative" style={{ minHeight: 360 }}>
            {streaming ? (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" data-testid="live-host-video" />
            ) : s.embed_url && s.status !== "scheduled" ? (
              <iframe
                src={s.embed_url}
                title="Live"
                className="w-full h-full"
                allow="autoplay; encrypted-media; camera; microphone"
                allowFullScreen
              />
            ) : s.status === "ended" && recordingSrc ? (
              <video src={recordingSrc} controls className="w-full h-full" data-testid="live-recording-video" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-500">
                <VideoCameraSlash size={40} weight="light" className="mb-3" />
                <div className="label-mono mb-1">{s.status === "ended" ? "NO RECORDING SAVED" : "NOT BROADCASTING"}</div>
                <p className="text-sm">{s.status === "scheduled" ? "Session has not started yet." : "Waiting for host..."}</p>
              </div>
            )}
            {recording && (
              <div className="absolute top-3 left-3 label-mono px-2 py-1 bg-black/80 border border-[#E60012] flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <Record size={10} weight="fill" className="pulse" /> RECORDING
              </div>
            )}
          </div>

          {/* Host controls */}
          {isHost && s.status !== "ended" && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="live-host-controls">
              {s.status === "scheduled" && !streaming && (
                <button onClick={startHosting} disabled={busy} className="btn btn-primary" data-testid="live-start-btn">
                  <Play size={13} weight="fill" /> Go live
                </button>
              )}
              {streaming && (
                <>
                  <button onClick={toggleRecord} className="btn" data-testid="live-record-toggle">
                    <Record size={13} weight={recording ? "fill" : "regular"} style={{ color: recording ? "#E60012" : "inherit" }} />
                    {recording ? " Stop recording" : " Record"}
                  </button>
                  <button onClick={endSession} disabled={busy} className="btn" data-testid="live-end-btn">
                    <Stop size={13} weight="fill" /> End session
                  </button>
                </>
              )}
            </div>
          )}

          {s.description && <p className="text-zinc-300 mt-4 text-sm leading-relaxed">{s.description}</p>}
        </section>

        {/* Side panel: participants + chat */}
        <aside className="lg:col-span-4 flex flex-col gap-4">
          <div className="border border-[#27272A]">
            <div className="px-4 py-2 border-b border-[#27272A] flex items-center justify-between">
              <span className="label-mono flex items-center gap-1.5"><Users size={11} /> PARTICIPANTS</span>
              <span className="label-mono">{participants.length}</span>
            </div>
            <div className="max-h-48 overflow-y-auto p-3 space-y-2" data-testid="live-participants">
              {participants.length === 0 ? (
                <div className="text-xs text-zinc-500 label-mono text-center py-3">NO ONE YET</div>
              ) : participants.map((p) => (
                <Link key={p.user_id} to={`/profile/${p.user_id}`} className="flex items-center gap-2 hover:bg-[#121214] p-1.5">
                  <div className="w-6 h-6 bg-[#27272A] flex items-center justify-center font-display text-xs text-white">{p.user_name[0]}</div>
                  <span className="text-xs text-white truncate">{p.user_name}</span>
                  {p.user_id === s.host_id && (
                    <span className="ml-auto label-mono" style={{ color: "#E60012", fontSize: 8 }}>HOST</span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          <div className="border border-[#27272A] flex-1 flex flex-col" style={{ maxHeight: 520 }}>
            <div className="px-4 py-2 border-b border-[#27272A] label-mono">LIVE CHAT</div>
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="live-chat-scroll">
              {msgs.length === 0 ? (
                <div className="text-xs text-zinc-500 label-mono text-center py-6">SAY SOMETHING</div>
              ) : msgs.map((m) => (
                <div key={m.id} className="text-sm" data-testid={`live-chat-msg-${m.id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-xs text-white">{m.author_name}</span>
                    {m.author_id === s.host_id && <span className="label-mono" style={{ color: "#E60012", fontSize: 8 }}>HOST</span>}
                    <span className="label-mono ml-auto">{fmtTime(m.created_at)}</span>
                  </div>
                  <div className="text-zinc-300 text-sm whitespace-pre-wrap break-words">{m.body}</div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="border-t border-[#27272A] p-2 flex gap-2" data-testid="live-chat-form">
              <input value={input} onChange={(e) => setInput(e.target.value)}
                disabled={!user || !user.id || s.status === "ended"}
                placeholder={user && user.id ? "Type a message..." : "Sign in to chat"}
                className="input py-1.5 text-sm flex-1"
                data-testid="live-chat-input"
              />
              <button type="submit" disabled={!input.trim() || !user || !user.id} className="btn btn-primary btn-sm" data-testid="live-chat-send">
                <PaperPlaneTilt size={11} weight="bold" />
              </button>
            </form>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
